<template>
  <div class="row h-100">
    <div class="col-md-8 offset-md-2 col-lg-6 offset-lg-3 col-xl-4 offset-xl-4">
      <div class="reuse-kyc-wrapper position-relative d-flex flex-column">
        <form @submit.prevent="handleContinueWithPhone">
          <div>
            <h1 v-if="isD2C" class="text-center fs-4 mt-5">
              {{ t("D2C_TERMS_TITLE") }}
            </h1>
            <p v-if="isD2C" class="text-center subtitle mb-5">
              {{ t("D2C_TERMS_SUBTITLE") }}
            </p>

            <SimplifiedPhoneInput
              :label-name="`${t('USER_CONTACT_PHONE_LABEL')} *`"
              class="my-3"
              input-id="phone-number"
              :country="merchantCountry"
              :disabled="false"
              :required="true"
              v-model="phoneNumber"
              @validate-emit="(value) => handlePhoneValidation(value)"
            />

            <InputFieldEmail
              v-if="showEmail"
              :label-name="`${t('CUSTOMER_CONTACT_INFO_EMAIL_LABEL')} (${t('OPTIONAL')})`"
              ref="emailComponent"
              :required="false"
              class="client_email"
              @validate-emit="(value) => (isInputEmailValid = value)"
              @input="(value) => (email = value)"
            />

            <Alert
              v-if="showAlert"
              :message="alertMessage"
              :alertType="alertType as any"
              :alertIcon="alertIcon"
              :closable="alertClosable"
              @onClose="dismissAlert"
            />
          </div>

          <div class="fixed-bottom-section">
            <TermsAndPrivacyContentModals
              :styleForSelfService="isSelfService"
              :locale="customerLocaleCode"
              :cartTag="cartTag"
            />

            <ButtonContinue
              class="btn-next mt-3 fw-bold fs-6"
              :class="{ 'fs-3 pj-ss-btn': isSelfService }"
              :isEnabled="
                isInputPhoneValid && isInputEmailValid && !isContinueInProgress
              "
            >
              <i
                v-if="isContinueWithPhoneInProgress"
                class="fa fa-spin fa-spinner"
              ></i>
              <span v-else>{{ t("CONTINUE") }}</span>
            </ButtonContinue>

            <div class="d-flex justify-content-center mt-4">
              <button
                v-if="shouldShowContinueWithoutPhoneButton"
                type="button"
                class="btn btn-sm btn-link pj-link-green text-decoration-none fw-bold fs-6"
                :disabled="isContinueInProgress"
                @click.prevent="handleContinueWithoutPhone"
              >
                <i
                  v-if="isContinueWithoutPhoneInProgress"
                  style="margin: 0"
                  class="fa fa-spin fa-spinner"
                ></i>
                {{ t("CONTINUE_WITHOUT_PHONE_NUMBER") }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <InAppBrowserRedirect :show="showInAppModal" :url="redirectUrl" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, getCurrentInstance, onMounted } from "vue";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { datadogRum } from "@datadog/browser-rum";
import axios from "axios";
import { useTermsAndPrivacyAcceptance } from "@/apps/merchant3/hooks/useTermsAndPrivacyAcceptance";
import { useAlert } from "@/apps/merchant3/hooks/useAlert";
import { useKycReuse } from "@/apps/merchant3/hooks/useKycReuse";
import { useCustomerLocale } from "@/apps/merchant3/hooks/useCustomerLocale";
import { useConfigurableNavigation } from "@/apps/merchant3/hooks/useConfigurableNavigation";
import { validatePhoneNumber } from "@/apps/merchant3/services/utils.service";
import { RouteName } from "@/apps/merchant3/router/RouteName";
import { CartActiveFeatures } from "@/apps/merchant3/types/CartActiveFeatures";
import Alert from "@/apps/merchant3/shared-components/common/Alert.vue";
import ButtonContinue from "@/apps/merchant3/shared-components/ui/ButtonContinue.vue";
import SimplifiedPhoneInput from "@/apps/merchant3/shared-components/input-fields/SimplifiedPhoneInput.vue";
import InputFieldEmail from "@/apps/merchant3/shared-components/input-fields/InputFieldEmail.vue";
import TermsAndPrivacyContentModals from "./TermsAndPrivacyContentModals.vue";
import InAppBrowserRedirect from "@/apps/merchant3/components/ui/modals/InAppBrowserRedirect.vue";
import { useProductVersion } from "@/apps/merchant3/hooks/useProductVersion";
import { isEcommerceFlow } from "@/apps/merchant3/helpers/checkoutFlows";
import { getAPIErrorMessage } from "@/apps/merchant3/shared-helpers/APIErrorMessages";
import { recorder, EVENTS } from "@/apps/merchant3/shared-services/recording.service";
import { pushToDataLayer } from "@/apps/merchant3/shared-helpers/dataLayer.helper";
import DataLayerEvents from "@/apps/merchant3/types/DataLayerEvents";
import { anonymizeData } from "@/apps/merchant3/shared-helpers/anonymize.helper";
import { createCart } from "@/apps/merchant3/services/carts.service";
import { identifyLead } from "@/apps/merchant3/services/customerContactInfo.service";
import { ProductVersion } from "@/apps/merchant3/types/ProductVersion";
import { SessionStorageEnum } from "@/apps/merchant3/enums/sessionStorageEnum";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import logAction from "@/apps/merchant3/services/action-logs.service";
import ActionLogs from "@/apps/merchant3/types/ActionLogEvents";
import { isDirectToCustomerSessionSet } from "@/apps/merchant3/helpers/navigationGuards";
import { getCountryCode } from "@/apps/merchant3/helpers/flowType";
import { getDeviceType } from "@/apps/merchant3/shared-helpers/device-detect.helper";

const { t } = useI18n();
const store = useStore();
const isD2C = computed(() => isDirectToCustomerSessionSet());
const router = useRouter();
const route = useRoute();
const app = getCurrentInstance()?.appContext.app;

const {
  showAlert,
  alertType,
  alertIcon,
  alertMessage,
  alertClosable,
  dismissAlert,
} = useAlert();
const { isSelfService } = useProductVersion();

const { customerLocaleCode } = useCustomerLocale();
const { acceptPrivacyAndTerms, handleContinueToNextPage } =
  useTermsAndPrivacyAcceptance();

const isInAppBrowser = getDeviceType() === "InAppBrowser";
const showInAppModal = ref(false);

const merchantCountry = computed(() => store.state.merchant?.merchant?.country);

const redirectUrl = computed(() => {
  const hasWindow =
    typeof window !== "undefined" && typeof window.location !== "undefined";
  const origin =
    hasWindow && typeof (window.location as any).origin === "string"
      ? (window.location as any).origin
      : "";
  const pathname =
    hasWindow && typeof (window.location as any).pathname === "string"
      ? (window.location as any).pathname
      : "";
  const basePath = String(pathname || "").includes("/frontend")
    ? "/frontend"
    : "";
  const countryCode = merchantCountry.value?.toLowerCase() || "mx";
  return origin ? `${origin}${basePath}/${countryCode}/d2c` : `${basePath}/${countryCode}/d2c`;
});
const cartTag = computed(() => store.state.carts.cartTag);
const phoneNumber = ref("");
const email = ref("");
const selectedCountryFromPhone = ref("");
const isInputPhoneValid = ref(false);
const isInputEmailValid = ref(true);
const isContinueWithPhoneInProgress = ref(false);
const isContinueWithoutPhoneInProgress = ref(false);
const isApproveTermsAndPrivacySuccess = ref(false);
const isCheckoutOnD2C = computed(() => {
  if (!cartTag.value) {
    return true;
  }
  return store.getters["carts/hasActiveFeature"](
    CartActiveFeatures.CHECKOUT_ON_DIRECT_TO_CUSTOMER,
  );
});
const isContinueInProgress = computed(
  () =>
    isContinueWithPhoneInProgress.value ||
    isContinueWithoutPhoneInProgress.value,
);

const shouldShowContinueWithoutPhoneButton = computed(() => {
  return !isCheckoutOnD2C.value;
});

const showEmail = computed(() => {
  return isCheckoutOnD2C.value;
});

const { findPersonsByPhone } = useKycReuse();
const { isConfigurableFlow, navigateToNextStep } = useConfigurableNavigation();

/**
 * Handle basic phone validation (format, length, etc.)
 */
const handlePhoneValidation = (isBasicValidationValid: boolean) => {
  isInputPhoneValid.value = isBasicValidationValid;
};

/**
 * Handle country selection change from phone input
 */
const handleCountryChange = (country) => {
  selectedCountryFromPhone.value = country.iso2;
};

const handleContinueWithPhone = async () => {
  const continueWithPhoneStart = performance.now();
  isContinueWithPhoneInProgress.value = true;
  showAlert.value = false;

  // Step 1: For D2C flow, create cart if it doesn't exist yet
  if (isDirectToCustomerSessionSet() && !cartTag.value) {
    try {
      const response = await createCart(ProductVersion.M3);
      store.dispatch("carts/setCart", response.data.cart);
      sessionStorage.setItem(
        SessionStorageEnum.CartTag,
        response.data.cart.cartTag as string,
      );

      const queryParams = route.query;
      logEvent(EventLogs.Events.CART_CREATED, {
        cartTag: response.data.cart.cartTag as string,
        queryParams: queryParams,
      });
      logAction(ActionLogs.Events.CART_CREATED);

      // Send lead info to CustomerIO (non-blocking, failures tracked in backend logs)
      identifyLead({
        phone_number: phoneNumber.value,
        ...(email.value && { email: email.value }),
      }).catch(() => {
        // Silently ignore errors - backend logs will track failures
      });
    } catch (error) {
      const thisError = error as Error;
      const errorMessage = axios.isAxiosError(thisError)
        ? getAPIErrorMessage(thisError?.response?.data, t)
        : thisError.message;

      isContinueWithPhoneInProgress.value = false;
      alertType.value = "alert-danger";
      alertMessage.value = errorMessage;
      showAlert.value = true;
      return;
    }
  }

  // Step 2: Validate the phone number integrity using the API
  try {
    // Use the country from the phone selector or fallback to merchant country
    const selectedCountryCode =
      selectedCountryFromPhone.value || merchantCountry.value;
    await validatePhoneNumber(phoneNumber.value, selectedCountryCode);
  } catch (error) {
    const thisError = error as Error;
    const errorMessage = axios.isAxiosError(thisError)
      ? getAPIErrorMessage(thisError?.response?.data, t)
      : thisError.message;

    isContinueWithPhoneInProgress.value = false;
    alertType.value = "alert-danger";
    alertMessage.value = errorMessage;
    showAlert.value = true;
    return;
  }

  // Step 3: After phone validation passes, handle privacy and terms acceptance
  await handlePrivaceAndTermsAcceptance();

  if (!isApproveTermsAndPrivacySuccess.value) {
    isContinueWithPhoneInProgress.value = false;
    return;
  }

  let matchFound = false;
  try {
    matchFound = await findPersonsByPhone(
      cartTag.value,
      phoneNumber.value,
      email.value,
    );

    const normalizedPhone = phoneNumber.value.replace(/[+\s]/g, "");
    const hashedPhone = await anonymizeData(normalizedPhone);

    const eventData: Record<string, unknown> = {
      payjoy_event: "landing_page_view",
      flow_type: "two_steps_funnel",
      country: merchantCountry.value,
    };

    if (hashedPhone) {
      eventData.sha256_phone = hashedPhone;
    }

    if (email.value && email.value.trim() !== "") {
      const hashedEmail = await anonymizeData(email.value);
      if (hashedEmail) {
        eventData.sha256_email = hashedEmail;
      }
    }

    pushToDataLayer(DataLayerEvents.LEADINFO_REGISTERED, eventData, false);
  } catch (error) {
    const thisError = error as Error;
    const errorMessage = axios.isAxiosError(thisError)
      ? getAPIErrorMessage(thisError?.response?.data, t)
      : thisError.message;

    isContinueWithPhoneInProgress.value = false;
    alertType.value = "alert-danger";
    alertMessage.value = errorMessage;
    showAlert.value = true;
    return;
  } finally {
    // Keep track of how long it took to continue with phone in RUM.
    const continueWithPhoneDuration =
      performance.now() - continueWithPhoneStart;
    datadogRum.addAction("m3-kyc-reuse-continue-with-phone-clicked", {
      loadTime: continueWithPhoneDuration,
    });
  }

  if (matchFound) {
    sessionStorage.setItem("kycReusePhoneNumber", phoneNumber.value);
    sessionStorage.setItem("kycReuseOrigin", "phoneNumber");
    // Use configurable DOB route when in configurable flow
    const dobRoute = isConfigurableFlow.value
      ? RouteName.M3ConfigurableDob
      : RouteName.M3DateOfBirthVerification;
    const countryCode = (route.params.countryCode as string)?.toLowerCase() || getCountryCode();
    router.push({ name: dobRoute, params: { countryCode } });
    return;
  }

  // For configurable flow, use backend-driven navigation
  if (isConfigurableFlow.value) {
    navigateToNextStep();
    return;
  }

  handleContinueToNextPage();
};

const handleContinueWithoutPhone = async () => {
  const continueWithoutPhoneStart = performance.now();

  isContinueWithoutPhoneInProgress.value = true;
  showAlert.value = false;

  try {
    await handlePrivaceAndTermsAcceptance();
  } finally {
    isContinueWithoutPhoneInProgress.value = false;
    // Keep track of how long it took to continue without phone in RUM.
    const continueWithoutPhoneDuration =
      performance.now() - continueWithoutPhoneStart;
    datadogRum.addAction("m3-kyc-reuse-continue-without-phone-clicked", {
      loadTime: continueWithoutPhoneDuration,
    });
  }

  if (!isApproveTermsAndPrivacySuccess.value) {
    return;
  }

  // Use configurable ID Number + DOB route when in configurable flow
  const idNumberDobRoute = isConfigurableFlow.value
    ? RouteName.M3ConfigurableIdNumberDob
    : RouteName.M3IdNumberDobVerification;
  const countryCode = (route.params.countryCode as string)?.toLowerCase() || getCountryCode();
  router.push({ name: idNumberDobRoute, params: { countryCode } });
};

const handlePrivaceAndTermsAcceptance = async () => {
  isApproveTermsAndPrivacySuccess.value = false;

  return acceptPrivacyAndTerms(
    cartTag.value,
    () => {
      isApproveTermsAndPrivacySuccess.value = true;
    },
    (message, _error) => {
      alertType.value = "alert-danger";
      alertMessage.value = message;
      showAlert.value = true;
      isApproveTermsAndPrivacySuccess.value = false;
    },
  );
};

onMounted(() => {
  if (isInAppBrowser) {
    showInAppModal.value = true;
    logEvent(EventLogs.Events.BROWSER_REDIRECTION_POPUP_SHOWN);
    return;
  }

  recorder(app)?.recordEvent(EVENTS.KYC_REUSE_CART_CREATED);

  if (isEcommerceFlow.value) {
    pushToDataLayer(
      DataLayerEvents.LEADINFO_VIEW,
      {
        payjoy_event: DataLayerEvents.LEADINFO_VIEW,
        flow_type: "two_steps_funnel",
        country: merchantCountry.value,
      },
      false,
    );
  }
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.reuse-kyc-wrapper {
  padding-bottom: 200px;
}

.fixed-bottom-section {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  padding: 16px 24px;
  z-index: 10;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

.fixed-bottom-section .btn-next {
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  display: block;
}

h1 {
  color: $pj-color-black;
}
.subtitle {
  color: $pj-color-gray-2;
}
:deep(.form-label) {
  color: $pj-color-gray-2;
}
.btn-link:hover {
  color: #2c7040;
}
.btn-link:active {
  color: #2c7040 !important;
}
.btn-link:focus {
  color: #2c7040;
}
.btn-link:focus-visible {
  color: #2c7040;
}
</style>
